// Enhanced api.js with improved error handling
import axios from 'axios';

// Create an axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001'
});

// Flag to track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];

// Process the failed queue
const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If the error is not 401 or it's already been retried, just reject
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }
    
    // Mark the request as retried
    originalRequest._retry = true;
    
    // If we're already refreshing, queue this request
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch(err => Promise.reject(err));
    }
    
    // Start refreshing
    isRefreshing = true;
    
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        // No refresh token, but don't redirect here
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('loginTimestamp');
        // Don't redirect directly - let the component handle it
        processQueue(new Error('Failed to refresh token'));
        return Promise.reject(error);
      }
      
      // Try to refresh the token
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/refresh-token`, 
        { refreshToken }
      );
      
      if (response.data && response.data.accessToken) {
        localStorage.setItem('token', response.data.accessToken);
        localStorage.setItem('loginTimestamp', Date.now());
        
        originalRequest.headers.Authorization = `Bearer ${response.data.accessToken}`;
        
        processQueue(null, response.data.accessToken);
        
        return api(originalRequest);
      } else {
        // Invalid response, but don't redirect here
        processQueue(new Error('Failed to refresh token'));
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('loginTimestamp');
        return Promise.reject(error);
      }
    } catch (refreshError) {
      // Error refreshing token, but don't redirect here
      processQueue(refreshError);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('loginTimestamp');
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;